import React, { useEffect, useMemo, useRef, useState } from 'react';
import styled from '@emotion/styled';
import ExpandMoreRoundedIcon from '@mui/icons-material/ExpandMoreRounded';
import DOMPurify from 'dompurify';
import { PRIMARY } from './detailStyles';

/*
 * 세미나 설명·채용 상세 내용(2026-10-01~) — 세로가 가로의 3배를 넘는 긴 이미지(포스터)가 있으면 그 이미지 시작점에서 foldHeight(모바일 400 / PC 560 — descriptionFold.ts)만 보이고 접는다.
 * 접힌 끝에 120px 흰 그라데이션, 그 위에 걸치는 "상세 설명 더보기 ⌄" 버튼(44px, 1px #d1d5db, 회색 글자, 반경 8px).
 * 긴 이미지가 없으면 접지 않는다.
 *
 * 긴 이미지 위쪽 파생본(서버가 설명 이미지 업로드 때 만든 `{원본}_top.jpg`, 위쪽 3:4 — 2026-10-01 전에 올린 것은 4:3):
 * - 접힌 상태에서는 본문 첫 이미지를 파생본으로 그린다(원본은 요청하지 않는다). 파생본이 없으면(404·403 등) 원본으로 폴백.
 * - 파생본이 뜨면 그 이미지는 긴 이미지로 보고 접는다(서버는 세로 > 가로×1.5 일 때만 만든다).
 * - "상세 설명 더보기"를 누르면 원본을 미리 받아 두었다가 다 받으면 바꾼다(그동안 파생본 유지 — 깜빡임 없음).
 */

/** 파생본을 가리키는 표시 — 원본 주소를 들고 있다 */
const ORIGINAL_ATTR: string = 'data-original-src';

/** 원본 주소 → 위쪽 파생본 주소(.jpg/.jpeg/.png/.webp 앞에 _top, 확장자는 .jpg). 규칙에 안 맞으면 null */
const topVariantUrl = (src: string): string | null => {
  if (!src || /_top\.jpg($|[?#])/i.test(src)) return null;
  const match: RegExpMatchArray | null = src.match(/^(.*)\.(jpe?g|png|webp)(?=$|[?#])/i);
  return match ? `${match[1]}_top.jpg` : null;
};

/** 본문 HTML 의 첫 이미지를 파생본으로 바꾼다(원본 주소는 data-original-src 에 둔다) */
const withTopVariant = (html: string): string => {
  if (typeof DOMParser === 'undefined') return html;
  const doc: Document = new DOMParser().parseFromString(html, 'text/html');
  const first: HTMLImageElement | null = doc.body.querySelector('img');
  const src: string | null = first?.getAttribute('src') ?? null;
  const top: string | null = src ? topVariantUrl(src) : null;
  if (!first || !src || !top) return html;
  first.setAttribute(ORIGINAL_ATTR, src);
  first.setAttribute('src', top);
  return doc.body.innerHTML;
};

const TALL_RATIO: number = 3;
const FADE_HEIGHT_PX: number = 120;
/** 접어도 이만큼도 안 줄면 접지 않는다 */
const MIN_HIDDEN_PX: number = 40;

const ContentBox = styled.div`
  position: relative;
`;

const Content = styled.div`
  font-size: 14px;
  line-height: 1.7;
  color: #374151;
  word-break: keep-all;
  overflow-wrap: anywhere;

  h1, h2, h3, h4, h5, h6 {
    margin: 16px 0 8px;
    color: #111827;
  }

  h1 { font-size: 1.3rem; }
  h2 { font-size: 1.15rem; }
  h3 { font-size: 1rem; }

  p {
    margin: 6px 0;
  }

  ul, ol {
    margin: 8px 0;
    padding-left: 20px;
  }

  li {
    margin: 4px 0;
  }

  strong {
    font-weight: 600;
  }

  a {
    color: ${PRIMARY};
    text-decoration: none;

    &:hover {
      text-decoration: underline;
    }
  }

  img {
    display: block;
    max-width: 100%;
    height: auto;
    border-radius: 8px;
    margin: 10px 0;
  }

  /* 에디터 정렬(Quill) — 이미지는 block 이라 글 정렬 대신 좌우 여백으로 맞춘다 */
  .ql-align-center { text-align: center; }
  .ql-align-right { text-align: right; }
  .ql-align-justify { text-align: justify; }
  .ql-align-center img { margin-left: auto; margin-right: auto; }
  .ql-align-right img { margin-left: auto; margin-right: 0; }

  blockquote {
    border-left: 4px solid ${PRIMARY};
    margin: 12px 0;
    padding: 6px 12px;
    background: #f3f4f6;
  }

  pre {
    background: #f3f4f6;
    padding: 10px;
    border-radius: 4px;
    overflow-x: auto;
  }
`;

const Fade = styled.div`
  position: absolute;
  left: 0;
  right: 0;
  bottom: 0;
  height: ${FADE_HEIGHT_PX}px;
  background: linear-gradient(rgba(255, 255, 255, 0), #fff);
  pointer-events: none;
`;

/** 글자 + 아래 꺾쇠 SVG — 예전 테두리 회전 꺾쇠(가상 요소)는 글꼴·회전 때문에 묶음 가운데가 6.5px 어긋났다(C8, 가이드 4.1) */
const MoreButton = styled.button`
  position: relative;
  z-index: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 2px;
  width: 100%;
  height: 44px;
  margin-top: -22px;
  padding: 0;
  border: 1px solid #d1d5db;
  border-radius: 8px;
  background: #fff;
  color: #374151;
  font-family: inherit;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;

  svg {
    display: block;
    flex: 0 0 18px;
    width: 18px;
    height: 18px;
    color: #6b7280;
  }
`;

interface CollapsibleDescriptionProps {
  html: string;
  foldHeight: number;
  /** 본문 글자 모양 — 없으면 세미나 설명 모양. 채용 상세는 자기 본문 모양(RichTextContent)을 넘긴다 */
  ContentComponent?: React.ElementType;
}

const CollapsibleDescription: React.FC<CollapsibleDescriptionProps> = ({ html, foldHeight, ContentComponent = Content }) => {
  const contentRef = useRef<HTMLDivElement>(null);
  const [foldAt, setFoldAt] = useState<number | null>(null);
  const [expanded, setExpanded] = useState(false);
  // 첫 이미지를 파생본으로 바꾼 HTML — 같은 문자열이면 React 가 innerHTML 을 다시 쓰지 않는다(원본 교체가 유지된다)
  const renderedHtml: string = useMemo(() => withTopVariant(DOMPurify.sanitize(html)), [html]);

  useEffect(() => {
    const element: HTMLDivElement | null = contentRef.current;
    if (!element) return undefined;

    const measure = (): void => {
      // 파생본이 떴으면 긴 이미지 — 그 이미지 시작점에서 foldHeight(파생본이 더 짧으면 파생본 끝)까지 보이고 접는다
      const top: HTMLImageElement | null = element.querySelector<HTMLImageElement>(`img[${ORIGINAL_ATTR}]`);
      if (top) {
        if (!top.complete || top.naturalWidth === 0) return; // 아직 로딩 중(실패면 onerror 가 원본으로 바꾼다)
        const offset: number = top.getBoundingClientRect().top - element.getBoundingClientRect().top;
        setFoldAt(Math.round(offset + Math.min(foldHeight, top.getBoundingClientRect().height)));
        return;
      }
      const images: HTMLImageElement[] = Array.from(element.querySelectorAll('img'));
      const tall: HTMLImageElement | undefined = images.find(
        (image: HTMLImageElement) => image.naturalWidth > 0 && image.naturalHeight > image.naturalWidth * TALL_RATIO,
      );
      if (!tall) {
        setFoldAt(null);
        return;
      }
      const offset: number = tall.getBoundingClientRect().top - element.getBoundingClientRect().top;
      const at: number = Math.round(offset + foldHeight);
      setFoldAt(element.scrollHeight > at + MIN_HIDDEN_PX ? at : null);
    };

    // 파생본이 없으면(404·403 등) 원본으로 — 그 뒤로는 원래 규칙(세로 > 가로×3)으로 접을지 정한다
    const topImage: HTMLImageElement | null = element.querySelector<HTMLImageElement>(`img[${ORIGINAL_ATTR}]`);
    const fallback = (): void => {
      if (!topImage) return;
      const original: string | null = topImage.getAttribute(ORIGINAL_ATTR);
      topImage.removeAttribute(ORIGINAL_ATTR);
      if (original) {
        topImage.addEventListener('load', measure, { once: true });
        topImage.src = original;
      }
    };
    if (topImage) {
      if (topImage.complete && topImage.naturalWidth === 0) fallback();
      else topImage.addEventListener('error', fallback, { once: true });
    }

    measure();
    const pending: HTMLImageElement[] = Array.from(element.querySelectorAll('img')).filter(
      (image: HTMLImageElement) => !image.complete,
    );
    pending.forEach((image: HTMLImageElement) => image.addEventListener('load', measure));
    return () => {
      topImage?.removeEventListener('error', fallback);
      pending.forEach((image: HTMLImageElement) => image.removeEventListener('load', measure));
    };
  }, [renderedHtml, foldHeight]);

  const expand = (): void => {
    setExpanded(true);
    // 원본을 미리 받아 두었다가 다 받으면 바꾼다 — 그동안은 파생본 그대로(깜빡임 없음). 한 번 바꾸면 다시 접어도 원본 유지
    const top: HTMLImageElement | null | undefined = contentRef.current?.querySelector<HTMLImageElement>(`img[${ORIGINAL_ATTR}]`);
    const original: string | null | undefined = top?.getAttribute(ORIGINAL_ATTR);
    if (!top || !original) return;
    const preload: HTMLImageElement = new Image();
    preload.onload = () => {
      top.src = original;
      top.removeAttribute(ORIGINAL_ATTR);
    };
    preload.src = original;
  };

  const collapsed: boolean = foldAt !== null && !expanded;

  return (
    <>
      <ContentBox>
        <ContentComponent
          ref={contentRef}
          style={collapsed ? { maxHeight: foldAt ?? undefined, overflow: 'hidden' } : undefined}
          dangerouslySetInnerHTML={{ __html: renderedHtml }}
        />
        {collapsed && <Fade />}
      </ContentBox>
      {collapsed && (
        <MoreButton type="button" aria-expanded={false} onClick={expand}>
          상세 설명 더보기
          <ExpandMoreRoundedIcon aria-hidden="true" />
        </MoreButton>
      )}
    </>
  );
};

export default CollapsibleDescription;
